/**
 * Homly — PageHeader, SectionBlock, HomlyButton, HelpHint
 * =======================================================
 * Composants de structure et CTA cohérents pour toutes les pages.
 */
import React from 'react';
import { HelpCircle } from 'lucide-react';

// ─── PageHeader ──────────────────────────────────────────────────────────
// Pattern : Kicker (ex: "Pilotage agence") · Titre · Sous-titre court · Action primaire
export function PageHeader({
    kicker,
    title,
    subtitle,
    primaryAction,         // { label, onClick, icon }
    secondaryAction,
    children,              // contenu à droite (filtres, tabs custom...)
    className = '',
    dataTestid = 'page-header',
}) {
    return (
        <header
            className={`flex flex-col md:flex-row items-stretch md:items-start justify-between gap-4 pb-5 mb-5 border-b border-[var(--homly-border-subtle)] ${className}`}
            data-testid={dataTestid}
            style={{
                fontFamily: 'var(--homly-font-body)',
            }}
        >
            <div style={{ minWidth: 0, flex: 1 }}>
                {kicker && (
                    <p
                        style={{
                            margin: 0,
                            marginBottom: 6,
                            fontFamily: 'var(--homly-font-display)',
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: 'var(--homly-orange)',
                        }}
                    >
                        {kicker}
                    </p>
                )}
                <h1
                    style={{
                        margin: 0,
                        fontFamily: 'var(--homly-font-display)',
                        fontSize: 'clamp(20px, 5vw, 28px)',
                        fontWeight: 700,
                        lineHeight: 1.15,
                        color: 'var(--homly-text-primary)',
                        letterSpacing: '-0.01em',
                    }}
                >
                    {title}
                </h1>
                {subtitle && (
                    <p
                        style={{
                            margin: '8px 0 0 0',
                            maxWidth: 720,
                            fontSize: 'clamp(13px, 3.6vw, 14.5px)',
                            lineHeight: 1.55,
                            color: 'var(--homly-text-secondary)',
                        }}
                    >
                        {subtitle}
                    </p>
                )}
            </div>

            {/* Actions à droite (passent en row pleine largeur sur mobile) */}
            <div
                className="flex items-center gap-2 sm:gap-3 flex-wrap md:flex-nowrap shrink-0"
                style={{ minWidth: 0 }}
            >
                {children}
                {secondaryAction && (
                    <HomlyButton variant="ghost" onClick={secondaryAction.onClick} icon={secondaryAction.icon}>
                        {secondaryAction.label}
                    </HomlyButton>
                )}
                {primaryAction && (
                    <HomlyButton variant="primary" onClick={primaryAction.onClick} icon={primaryAction.icon} dataTestid={`${dataTestid}-primary`}>
                        {primaryAction.label}
                    </HomlyButton>
                )}
            </div>
        </header>
    );
}

// ─── SectionBlock ────────────────────────────────────────────────────────
// Pattern : Kicker · Titre · Phrase d'aide · Actions secondaires · Contenu
export function SectionBlock({
    kicker,
    title,
    helpText,
    rightSlot,            // node (badge, lien, action discrète)
    children,
    spacing = 'normal',   // 'compact' | 'normal' | 'large'
    className = '',
    dataTestid,
}) {
    const spacings = {
        compact: { gap: 12, mb: 16 },
        normal: { gap: 16, mb: 24 },
        large: { gap: 20, mb: 32 },
    };
    const s = spacings[spacing] || spacings.normal;

    return (
        <section
            className={className}
            data-testid={dataTestid}
            style={{
                marginBottom: s.mb,
                fontFamily: 'var(--homly-font-body)',
            }}
        >
            {(kicker || title || helpText || rightSlot) && (
                <header
                    className="flex flex-col sm:flex-row sm:items-end sm:justify-between flex-wrap"
                    style={{ gap: 16, marginBottom: s.gap }}
                >
                    <div style={{ minWidth: 0, flex: 1 }}>
                        {kicker && (
                            <p
                                style={{
                                    margin: 0,
                                    marginBottom: 4,
                                    fontFamily: 'var(--homly-font-display)',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.18em',
                                    textTransform: 'uppercase',
                                    color: 'var(--homly-purple)',
                                }}
                            >
                                {kicker}
                            </p>
                        )}
                        {title && (
                            <h2
                                style={{
                                    margin: 0,
                                    fontFamily: 'var(--homly-font-display)',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: 'var(--homly-text-primary)',
                                    lineHeight: 1.25,
                                }}
                            >
                                {title}
                            </h2>
                        )}
                        {helpText && (
                            <p
                                style={{
                                    margin: '4px 0 0 0',
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    color: 'var(--homly-text-tertiary)',
                                    maxWidth: 640,
                                }}
                            >
                                {helpText}
                            </p>
                        )}
                    </div>
                    {rightSlot && <div>{rightSlot}</div>}
                </header>
            )}
            <div>{children}</div>
        </section>
    );
}

// ─── HomlyButton ─────────────────────────────────────────────────────────
// CTA unifié : 'primary' (gradient signature), 'secondary' (orange), 'ghost' (transparent), 'destructive'
export function HomlyButton({
    variant = 'primary',
    size = 'md',
    icon: Icon,
    iconRight: IconRight,
    children,
    onClick,
    disabled,
    type = 'button',
    className = '',
    dataTestid,
    fullWidth,
    ...rest
}) {
    const variants = {
        primary: {
            background: 'var(--homly-gradient-primary)',
            color: 'white',
            border: 'none',
            boxShadow: 'var(--homly-shadow-glow-purple)',
        },
        secondary: {
            background: 'var(--homly-gradient-orange)',
            color: 'white',
            border: 'none',
            boxShadow: 'var(--homly-shadow-glow-orange)',
        },
        ghost: {
            background: 'transparent',
            color: 'var(--homly-text-secondary)',
            border: '1px solid var(--homly-border-strong)',
            boxShadow: 'none',
        },
        outline: {
            background: 'transparent',
            color: 'var(--homly-purple)',
            border: '1px solid var(--homly-purple)',
            boxShadow: 'none',
        },
        destructive: {
            background: 'var(--homly-priority-critical)',
            color: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(255, 95, 87, 0.30)',
        },
    };
    const sizes = {
        sm: { padding: '6px 12px', fontSize: 12, gap: 5, iconSize: 13 },
        md: { padding: '10px 18px', fontSize: 13.5, gap: 7, iconSize: 16 },
        lg: { padding: '14px 24px', fontSize: 15, gap: 8, iconSize: 18 },
    };
    const v = variants[variant] || variants.primary;
    const s = sizes[size] || sizes.md;

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={`homly-focusable ${className}`}
            data-testid={dataTestid}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: s.gap,
                padding: s.padding,
                fontFamily: 'var(--homly-font-display)',
                fontSize: s.fontSize,
                fontWeight: 700,
                letterSpacing: '0.005em',
                borderRadius: 'var(--homly-radius-md)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                transition: 'transform var(--homly-transition-fast), box-shadow var(--homly-transition-fast)',
                width: fullWidth ? '100%' : undefined,
                whiteSpace: 'nowrap',
                ...v,
            }}
            onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            {...rest}
        >
            {Icon && <Icon size={s.iconSize} strokeWidth={2.4} aria-hidden="true" />}
            {children}
            {IconRight && <IconRight size={s.iconSize} strokeWidth={2.4} aria-hidden="true" />}
        </button>
    );
}

// ─── HelpHint ────────────────────────────────────────────────────────────
// Petite bulle d'aide contextuelle pour expliquer un terme métier ou un KPI.
// Réutilisable partout : à côté d'un titre, dans un label, etc.
export function HelpHint({ children, ariaLabel = 'Information', className = '' }) {
    const [open, setOpen] = React.useState(false);
    return (
        <span
            className={className}
            style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
            }}
        >
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                onBlur={() => setTimeout(() => setOpen(false), 200)}
                aria-label={ariaLabel}
                style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    display: 'flex',
                    color: 'var(--homly-text-tertiary)',
                }}
            >
                <HelpCircle size={13} strokeWidth={2.2} />
            </button>
            {open && (
                <span
                    role="tooltip"
                    style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%) translateY(-6px)',
                        minWidth: 220,
                        maxWidth: 280,
                        padding: '10px 12px',
                        background: 'var(--homly-bg-card-2)',
                        color: 'var(--homly-text-primary)',
                        border: '1px solid var(--homly-border-strong)',
                        borderRadius: 'var(--homly-radius-md)',
                        fontSize: 12,
                        lineHeight: 1.45,
                        boxShadow: 'var(--homly-shadow-md)',
                        zIndex: 50,
                        whiteSpace: 'normal',
                    }}
                >
                    {children}
                </span>
            )}
        </span>
    );
}
